import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, eur, fmtDate } from "../lib/api";
import { Hammer, ArrowLeft, TrendingUp, Receipt, CheckCircle2, Clock } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Projects() {
  const [list, setList] = useState(null);

  useEffect(() => {
    api.get("/projects").then(r => setList(r.data)).catch(() => setList([]));
  }, []);

  if (list === null) return <div className="text-white/50">Cargando...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      <header className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Control de obra</div>
          <h1 className="font-display font-bold text-4xl tracking-tight">Obras / Proyectos</h1>
          <p className="text-white/50 text-sm mt-2">Seguimiento de cada obra con totales certificados, facturados y cobrados.</p>
        </div>
      </header>

      {list.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-16 text-center" data-testid="projects-empty">
          <Hammer className="w-12 h-12 mx-auto text-white/20 mb-3" strokeWidth={1} />
          <div className="text-white/60 mb-4">Aún no tienes obras. Crea una factura con un nombre de obra para empezar.</div>
          <Link to="/app/facturas/nueva">
            <Button className="bg-brand-orange hover:bg-brand-orange-hover">Nueva factura</Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(p => (
            <Link key={p.name} to={`/app/obras/${encodeURIComponent(p.name)}`} data-testid={`project-card-${p.name}`}
              className="p-6 rounded-lg border border-white/10 bg-[#0A1128]/40 hover:border-brand-cyan/40 hover:-translate-y-1 transition-all duration-300">
              <Hammer className="w-5 h-5 text-brand-cyan mb-3" strokeWidth={1.5} />
              <h3 className="font-display font-semibold text-lg truncate mb-2">{p.name}</h3>
              <div className="text-sm text-white/60 mb-3">{p.invoices_count} {p.invoices_count === 1 ? "factura" : "facturas"}</div>
              <div className="font-display font-bold text-2xl text-brand-cyan">{eur(p.billed_total)}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-white/40 mt-1">Facturado</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProjectDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/projects/${encodeURIComponent(name)}/kpis`).then(r => setData(r.data));
  }, [name]);

  if (!data) return <div className="text-white/50">Cargando...</div>;
  const decoded = decodeURIComponent(name);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      <Button variant="ghost" onClick={() => navigate("/app/obras")} className="text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Todas las obras</Button>
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Obra</div>
        <h1 className="font-display font-bold text-4xl tracking-tight">{decoded}</h1>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Certificado a origen" value={eur(data.total_certified)} testid="kpi-certified" />
        <Kpi icon={Receipt} label="Facturado" value={eur(data.total_billed)} testid="kpi-billed" />
        <Kpi icon={CheckCircle2} label="Cobrado" value={eur(data.total_paid)} accent="orange" testid="kpi-paid" />
        <Kpi icon={Clock} label="Pendiente cobro" value={eur(data.total_pending)} accent="orange" testid="kpi-pending" />
      </div>

      {data.contract_total > 0 && (() => {
        const ct = data.contract_total;
        const certified = data.total_certified || 0;
        const remaining = ct - certified;
        const pct = Math.min(100, (certified / ct) * 100);
        const over = remaining < 0;
        return (
          <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-6" data-testid="contract-progress-card">
            <div className="flex justify-between items-end flex-wrap gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-cyan mb-1">Avance del contrato</div>
                <h2 className="font-display font-semibold text-lg">Total contrato: {eur(ct)}</h2>
              </div>
              <div className={`font-display font-bold text-2xl ${over ? "text-red-400" : "text-emerald-400"}`}>
                {over ? "Excedido" : "Pendiente"}: {eur(Math.abs(remaining))}
              </div>
            </div>
            <div className="h-3 bg-black/40 rounded overflow-hidden">
              <div className={`h-full transition-all ${over ? "bg-red-500" : pct > 90 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <div className="flex justify-between text-xs mt-2">
              <span className="text-white/60">Facturado a la fecha: <strong className="text-white">{eur(certified)}</strong></span>
              <span className="text-white/60 font-mono">{pct.toFixed(1)}%</span>
            </div>
          </section>
        );
      })()}

      {data.monthly?.length > 0 && (
        <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-6">
          <h2 className="font-display font-semibold text-lg mb-4">Evolución mensual</h2>
          <div className="space-y-2">
            {data.monthly.map(m => {
              const max = Math.max(...data.monthly.map(x => x.total));
              const w = (m.total / max) * 100;
              return (
                <div key={m.month} className="flex items-center gap-3">
                  <div className="text-xs text-white/50 w-16 font-mono">{m.month}</div>
                  <div className="flex-1 h-7 bg-black/40 rounded relative overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-brand-cyan to-brand-orange" style={{ width: `${w}%` }} />
                  </div>
                  <div className="text-sm font-semibold w-24 text-right">{eur(m.total)}</div>
                  <div className="text-xs text-white/40 w-20 text-right">{m.count} fact.</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h2 className="font-display font-semibold">Facturas de la obra</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-white/10 text-[10px] uppercase tracking-widest text-white/50">
              <th className="p-4 font-bold">Nº</th>
              <th className="p-4 font-bold">Fecha</th>
              <th className="p-4 font-bold">Tipo</th>
              <th className="p-4 font-bold">Estado</th>
              <th className="p-4 font-bold text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.invoices?.map(i => (
              <tr key={i.id} onClick={() => navigate(`/app/facturas/${i.id}`)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
                <td className="p-4 font-mono text-xs">{i.number}</td>
                <td className="p-4 text-white/60">{fmtDate(i.issue_date || i.created_at)}</td>
                <td className="p-4 text-white/60">{i.is_origin ? "A origen" : "Normal"}</td>
                <td className="p-4"><span className={`px-2 py-1 rounded text-[10px] uppercase font-bold ${i.paid ? "bg-emerald-500/15 text-emerald-400" : "bg-brand-orange/15 text-brand-orange"}`}>{i.paid ? "cobrado" : "pendiente"}</span></td>
                <td className="p-4 text-right font-semibold">{eur(i.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, accent = "cyan", testid }) => (
  <div data-testid={testid} className="p-6 rounded-lg border border-white/10 bg-[#0A1128]/60">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">{label}</div>
      <Icon className={`w-4 h-4 ${accent === "orange" ? "text-brand-orange" : "text-brand-cyan"}`} strokeWidth={1.5} />
    </div>
    <div className="font-display font-bold text-2xl">{value}</div>
  </div>
);
